import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CutsInRangeDialog } from '@/components/common/CutsInRangeDialog';
import { PayrollReport } from '@/components/reports/PayrollReport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toDateInput = (d) => d.toISOString().slice(0, 10);
const startOfDayIso = (yyyymmdd) => `${yyyymmdd}T00:00:00.000Z`;
const endOfDayIso = (yyyymmdd) => `${yyyymmdd}T23:59:59.999Z`;

export const Reports = () => {
  const [salesReport, setSalesReport] = useState([]);
  const [commissionsReport, setCommissionsReport] = useState([]);
  const [topProductsReport, setTopProductsReport] = useState([]);
  const [topProductsShipReport, setTopProductsShipReport] = useState([]);
  const [shipmentsReport, setShipmentsReport] = useState([]);
  const [promosReport, setPromosReport] = useState({ totals: null, ranking: [] });
  const [wholesaleReport, setWholesaleReport] = useState({ ranking: [] });
  const [wholesaleDetailClient, setWholesaleDetailClient] = useState(null); // {client_id, name}
  const [wholesaleDetail, setWholesaleDetail] = useState({ ranking: [], detail: null });
  const [heatmap, setHeatmap] = useState(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cutsOpen, setCutsOpen] = useState(false);

  // Rango de fechas global (aplica a TODOS los reportes)
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [fromDate, setFromDate] = useState(toDateInput(monthAgo));
  const [toDate, setToDate] = useState(toDateInput(today));

  const dateParams = useCallback(() => {
    const p = new URLSearchParams();
    if (fromDate) p.set('start_date', startOfDayIso(fromDate));
    if (toDate) p.set('end_date', endOfDayIso(toDate));
    return p.toString();
  }, [fromDate, toDate]);

  const applyQuickRange = (days) => {
    const end = new Date();
    const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
    setFromDate(toDateInput(start));
    setToDate(toDateInput(end));
  };

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const qs = dateParams();
      const [salesRes, commissionsRes, topProductsRes, topProductsShipRes, shipmentsRes, promosRes, wholesaleRes] = await Promise.all([
        axios.get(`${API}/reports/sales?${qs}`, { withCredentials: true }),
        axios.get(`${API}/reports/commissions?${qs}`, { withCredentials: true }),
        axios.get(`${API}/reports/top-products?${qs}`, { withCredentials: true }),
        axios.get(`${API}/reports/top-products-shipments?${qs}`, { withCredentials: true }),
        axios.get(`${API}/reports/shipments-status?${qs}`, { withCredentials: true }),
        axios.get(`${API}/promotions/report?${qs}`, { withCredentials: true }),
        axios.get(`${API}/reports/wholesale-sales?${qs}`, { withCredentials: true }),
      ]);
      setSalesReport(salesRes.data);
      setCommissionsReport(commissionsRes.data);
      setTopProductsReport(topProductsRes.data.slice(0, 20));
      setTopProductsShipReport(topProductsShipRes.data);
      setShipmentsReport(shipmentsRes.data);
      setPromosReport(promosRes.data || { totals: null, ranking: [] });
      setWholesaleReport(wholesaleRes.data || { ranking: [] });
    } catch (e) {
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  }, [dateParams]);

  const fetchHeatmap = useCallback(async () => {
    setHeatmapLoading(true);
    try {
      const qs = dateParams();
      const { data } = await axios.get(`${API}/reports/shipments-heatmap?${qs}`, { withCredentials: true });
      setHeatmap(data);
    } catch (e) {
      toast.error('Error al cargar mapa de zonas');
    } finally {
      setHeatmapLoading(false);
    }
  }, [dateParams]);

  // Cargar reportes iniciales y refrescar en cambios de fecha
  useEffect(() => {
    fetchReports();
    // Refrescar heatmap si ya se cargó al menos una vez
    if (heatmap !== null) fetchHeatmap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const onHeatmapTab = () => {
    if (!heatmap) fetchHeatmap();
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <p>Cargando reportes...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-8">
        <CutsInRangeDialog open={cutsOpen} onClose={() => setCutsOpen(false)} qs={dateParams()} />
        <h1 className="text-4xl font-bold tracking-tight mb-4">Reportes</h1>

        {/* Rango de fechas GLOBAL (aplica a todos los reportes) */}
        <div className="bg-white border rounded p-3 mb-4 flex items-end gap-3 flex-wrap" data-testid="reports-date-range">
          <div>
            <Label className="text-xs">Desde</Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              max={toDate}
              className="h-9 w-40"
              data-testid="reports-from-date"
            />
          </div>
          <div>
            <Label className="text-xs">Hasta</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={fromDate}
              max={toDateInput(new Date())}
              className="h-9 w-40"
              data-testid="reports-to-date"
            />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {[
              { label: 'Hoy', days: 0 },
              { label: '7d', days: 7 },
              { label: '30d', days: 30 },
              { label: '90d', days: 90 },
              { label: '6m', days: 180 },
              { label: '1a', days: 365 },
            ].map(p => (
              <button
                key={p.label}
                type="button"
                onClick={() => applyQuickRange(p.days)}
                className="px-2.5 py-1 text-xs rounded bg-slate-100 hover:bg-violet-100 transition"
                data-testid={`quick-range-${p.days}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {(loading || heatmapLoading) && <span className="text-xs text-muted-foreground">cargando…</span>}
            <Button variant="outline" size="sm" onClick={fetchReports} data-testid="reports-refresh-btn">
              🔄 Refrescar
            </Button>
          </div>
        </div>

        <Tabs defaultValue="sales" className="space-y-6" onValueChange={(v) => v === 'heatmap' && onHeatmapTab()}>
          <TabsList data-testid="reports-tabs">
            <TabsTrigger value="sales" data-testid="sales-tab">Ventas</TabsTrigger>
            <TabsTrigger value="commissions" data-testid="commissions-tab">Comisiones</TabsTrigger>
            <TabsTrigger value="products" data-testid="products-tab">Productos Más Vendidos</TabsTrigger>
            <TabsTrigger value="products-ship" data-testid="products-ship-tab">📦 Productos en Envíos</TabsTrigger>
            <TabsTrigger value="shipments" data-testid="shipments-tab">Estado de Envíos</TabsTrigger>
            <TabsTrigger value="promos" data-testid="promos-tab">🏷️ Promociones</TabsTrigger>
            <TabsTrigger value="wholesale" data-testid="wholesale-tab">🏪 Mayoristas</TabsTrigger>
            <TabsTrigger value="heatmap" data-testid="heatmap-tab">📍 Top Zonas</TabsTrigger>
            <TabsTrigger value="payroll" data-testid="payroll-tab">👥 Nómina</TabsTrigger>
          </TabsList>

          <TabsContent value="sales">
            <div className="bg-white border border-border rounded-sm">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="text-xl font-semibold">Ventas por Vendedor</h2>
                <Button variant="outline" size="sm" onClick={() => setCutsOpen(true)} data-testid="sales-cuts-btn">🧾 Ver cortes incluidos</Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="sales-report-table">
                  <thead className="bg-slate-50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Vendedor</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Ventas</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Ingresos</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {salesReport.map((item) => (
                      <tr key={item.seller_id} className="hover:bg-slate-50 btn-transition">
                        <td className="px-4 py-3 text-sm font-medium">{item.seller_name}</td>
                        <td className="px-4 py-3 text-sm text-right">{item.sales_count}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold">${item.total_revenue.toFixed(2)}</td>
                      </tr>
                    ))}
                    {salesReport.length === 0 && (
                      <tr>
                        <td colSpan="3" className="px-4 py-8 text-center text-muted-foreground">
                          No hay datos de ventas
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="payroll"><PayrollReport qs={dateParams()} /></TabsContent>

          <TabsContent value="commissions">
            <div className="bg-white border border-border rounded-sm">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="text-xl font-semibold">Comisiones por Vendedor</h2>
                <Button variant="outline" size="sm" onClick={() => setCutsOpen(true)} data-testid="commissions-cuts-btn">🧾 Ver cortes incluidos</Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="commissions-report-table">
                  <thead className="bg-slate-50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Vendedor</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Total Comisión</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Pagado</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Pendiente</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {commissionsReport.map((item) => (
                      <tr key={item.seller_id} className="hover:bg-slate-50 btn-transition">
                        <td className="px-4 py-3 text-sm font-medium">{item.seller_name}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold">${item.total_commission.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-right text-success">${item.paid.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-right text-warning">${item.pending.toFixed(2)}</td>
                      </tr>
                    ))}
                    {commissionsReport.length === 0 && (
                      <tr>
                        <td colSpan="4" className="px-4 py-8 text-center text-muted-foreground">
                          No hay datos de comisiones
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="products">
            <div className="bg-white border border-border rounded-sm">
              <div className="p-4 border-b border-border">
                <h2 className="text-xl font-semibold">Top 20 Productos Más Vendidos</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="top-products-report-table">
                  <thead className="bg-slate-50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Producto</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Cantidad Vendida</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Ingresos</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {topProductsReport.map((item, index) => (
                      <tr key={item.product_id} className="hover:bg-slate-50 btn-transition">
                        <td className="px-4 py-3 text-sm text-muted-foreground">{index + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium">{item.product_name}</td>
                        <td className="px-4 py-3 text-sm text-right">{item.quantity_sold}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold">${item.revenue.toFixed(2)}</td>
                      </tr>
                    ))}
                    {topProductsReport.length === 0 && (
                      <tr>
                        <td colSpan="4" className="px-4 py-8 text-center text-muted-foreground">
                          No hay datos de productos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Productos más vendidos SOLO en envíos, filtrado por el rango global */}
          <TabsContent value="products-ship">
            <div className="bg-white border border-border rounded-sm" data-testid="products-ship-panel">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="text-xl font-semibold">📦 Top productos en envíos</h2>
                <span className="text-xs text-muted-foreground">{topProductsShipReport.length} productos · rango {fromDate} → {toDate}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="products-ship-table">
                  <thead className="bg-slate-50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Producto</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Cantidad</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Revenue</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600"># Envíos</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(() => {
                      const maxQty = Math.max(...topProductsShipReport.map(p => p.quantity_sold), 1);
                      return topProductsShipReport.map((item, index) => {
                        const pct = (item.quantity_sold / maxQty) * 100;
                        return (
                          <tr
                            key={item.product_id}
                            className="relative hover:bg-slate-50 btn-transition"
                            data-testid={`product-ship-row-${item.product_id}`}
                          >
                            <td className="px-4 py-3 text-sm font-semibold text-slate-500">
                              {index === 0 ? '🏆' : `#${index + 1}`}
                            </td>
                            <td className="px-4 py-3 text-sm relative">
                              <div className="absolute inset-y-0 left-0 bg-sky-100 opacity-50" style={{ width: `${pct}%` }} />
                              <span className="relative font-medium">{item.product_name}</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-bold">{item.quantity_sold}</td>
                            <td className="px-4 py-3 text-sm text-right text-emerald-700 font-semibold">
                              ${item.revenue.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-sky-700">{item.shipments_count}</td>
                          </tr>
                        );
                      });
                    })()}
                    {topProductsShipReport.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">
                          Sin envíos en el rango seleccionado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="shipments">
            <div className="bg-white border border-border rounded-sm">
              <div className="p-4 border-b border-border">
                <h2 className="text-xl font-semibold">Estado de Envíos por Repartidor</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="shipments-report-table">
                  <thead className="bg-slate-50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Repartidor</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Pendiente</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">En Camino</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Entregado</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Por Pagar</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Pagado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {shipmentsReport.map((item) => (
                      <tr key={item.deliverer_id} className="hover:bg-slate-50 btn-transition">
                        <td className="px-4 py-3 text-sm font-medium">{item.deliverer_name}</td>
                        <td className="px-4 py-3 text-sm text-right">{item.total_shipments}</td>
                        <td className="px-4 py-3 text-sm text-right">{item.pendiente}</td>
                        <td className="px-4 py-3 text-sm text-right">{item.en_camino}</td>
                        <td className="px-4 py-3 text-sm text-right">{item.entregado}</td>
                        <td className="px-4 py-3 text-sm text-right text-warning">${item.total_owed.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-right text-success">${item.total_paid.toFixed(2)}</td>
                      </tr>
                    ))}
                    {shipmentsReport.length === 0 && (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">
                          No hay datos de envíos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Promociones */}
          <TabsContent value="promos">
            <div className="space-y-4">
              {promosReport.totals && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="promos-summary">
                  <div className="bg-white border rounded p-3">
                    <div className="text-xs text-muted-foreground">Promos activas</div>
                    <div className="text-2xl font-bold text-emerald-700">{promosReport.totals.promos_activas}</div>
                  </div>
                  <div className="bg-white border rounded p-3">
                    <div className="text-xs text-muted-foreground">Ventas con promo</div>
                    <div className="text-2xl font-bold">{promosReport.totals.ventas_con_promo}</div>
                  </div>
                  <div className="bg-white border rounded p-3">
                    <div className="text-xs text-muted-foreground">Piezas vendidas</div>
                    <div className="text-2xl font-bold text-blue-700">{promosReport.totals.piezas_vendidas}</div>
                  </div>
                  <div className="bg-white border rounded p-3">
                    <div className="text-xs text-muted-foreground">Ingreso con promo</div>
                    <div className="text-2xl font-bold text-violet-700">${(promosReport.totals.ingreso_total_promo || 0).toFixed(2)}</div>
                    <div className="text-[10px] text-slate-400">vs base ${(promosReport.totals.ingreso_base_sin_promo || 0).toFixed(2)}</div>
                  </div>
                  <div className="bg-white border rounded p-3">
                    <div className="text-xs text-muted-foreground">Descuento otorgado</div>
                    <div className="text-2xl font-bold text-rose-700">${(promosReport.totals.descuento_total_otorgado || 0).toFixed(2)}</div>
                    <div className="text-[10px] text-slate-400">total ahorrado al cliente</div>
                  </div>
                </div>
              )}
              <div className="bg-white border border-border rounded-sm">
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Ranking de promociones</h2>
                  <span className="text-xs text-muted-foreground">Ordenado por piezas vendidas</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="promos-report-table">
                    <thead className="bg-slate-50 border-b border-border">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">#</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Producto</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Tipo</th>
                        <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Ventas</th>
                        <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Piezas</th>
                        <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Ingreso</th>
                        <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Ingreso Base</th>
                        <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Descuento</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Cupo</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(promosReport.ranking || []).map((r, idx) => (
                        <tr key={r.promo_id} className="hover:bg-slate-50 btn-transition" data-testid={`promo-row-${r.promo_id}`}>
                          <td className="px-3 py-3 text-sm font-bold text-slate-500">{idx + 1}</td>
                          <td className="px-3 py-3 text-sm">
                            <div className="flex items-center gap-2">
                              {r.image_url ? (
                                <img src={r.image_url} alt={r.name} className="w-8 h-8 rounded object-cover border" loading="lazy" />
                              ) : (
                                <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-slate-300">—</div>
                              )}
                              <div>
                                <div className="font-medium truncate max-w-[220px]">{r.name}</div>
                                {r.sku && <div className="text-[10px] text-muted-foreground">SKU {r.sku}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-sm">
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-slate-100 border border-slate-200">
                              {({flat:'💵 Flat', min_qty:'🔢 Min qty', percent_discount:'🏷️ % Desc.', limited_qty:'🔥 Limitada', gift:'🎁 Regalo'})[r.type] || r.type || '—'}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-sm text-right">{r.count_sales}</td>
                          <td className="px-3 py-3 text-sm text-right font-bold text-blue-700">{r.total_qty}</td>
                          <td className="px-3 py-3 text-sm text-right text-violet-700 font-semibold">${(r.revenue||0).toFixed(2)}</td>
                          <td className="px-3 py-3 text-sm text-right text-slate-500">${(r.base_revenue||0).toFixed(2)}</td>
                          <td className="px-3 py-3 text-sm text-right text-rose-700 font-semibold">${(r.discount_given||0).toFixed(2)}</td>
                          <td className="px-3 py-3 text-sm">
                            {r.qty_limit ? (
                              <span className="text-[11px] font-medium">
                                {r.qty_sold||0}/{r.qty_limit}
                              </span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-3 text-sm">
                            {r.active
                              ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-300">Activa</span>
                              : <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">Inactiva</span>
                            }
                          </td>
                        </tr>
                      ))}
                      {(!promosReport.ranking || promosReport.ranking.length === 0) && (
                        <tr>
                          <td colSpan="10" className="px-4 py-10 text-center text-muted-foreground">
                            No hay promociones aplicadas en este rango de fechas.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Mayoristas */}
          <TabsContent value="wholesale">
            <div className="space-y-4">
              {!wholesaleDetailClient ? (
                <div className="bg-white border border-border rounded-sm">
                  <div className="p-4 border-b border-border flex items-center justify-between">
                    <h2 className="text-xl font-semibold">Ventas por mayorista</h2>
                    <span className="text-xs text-muted-foreground">Suma padre + hijos</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="wholesale-report-table">
                      <thead className="bg-slate-50 border-b border-border">
                        <tr>
                          <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">#</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Mayorista</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Ventas</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Piezas</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Importe</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Sub-cuentas</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Detalle</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {(wholesaleReport.ranking || []).map((r, idx) => (
                          <tr key={r.client_id} className="hover:bg-slate-50" data-testid={`wholesale-row-${r.client_id}`}>
                            <td className="px-3 py-3 text-sm font-bold text-slate-500">{idx + 1}</td>
                            <td className="px-3 py-3 text-sm font-medium">{r.client_name}</td>
                            <td className="px-3 py-3 text-sm text-right">{r.count_sales}</td>
                            <td className="px-3 py-3 text-sm text-right font-bold text-blue-700">{r.total_qty}</td>
                            <td className="px-3 py-3 text-sm text-right text-violet-700 font-semibold">${(r.total_amount||0).toFixed(2)}</td>
                            <td className="px-3 py-3 text-sm text-right">
                              {(r.by_child || []).filter(c => c.is_child).length}
                            </td>
                            <td className="px-3 py-3 text-sm text-right">
                              <button
                                onClick={async () => {
                                  setWholesaleDetailClient({ client_id: r.client_id, name: r.client_name });
                                  try {
                                    const qs = dateParams();
                                    const { data } = await axios.get(`${API}/reports/wholesale-sales?${qs}&client_id=${r.client_id}`, { withCredentials: true });
                                    setWholesaleDetail(data);
                                  } catch { toast.error('No se pudo cargar el detalle'); }
                                }}
                                className="text-violet-700 hover:underline text-xs font-medium"
                                data-testid={`wholesale-view-${r.client_id}`}
                              >Ver detalle →</button>
                            </td>
                          </tr>
                        ))}
                        {(!wholesaleReport.ranking || wholesaleReport.ranking.length === 0) && (
                          <tr>
                            <td colSpan="7" className="px-4 py-10 text-center text-muted-foreground">
                              Sin compras de mayoristas en este rango.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <button onClick={() => { setWholesaleDetailClient(null); setWholesaleDetail({ ranking: [], detail: null }); }} className="text-sm text-violet-700 hover:underline" data-testid="wholesale-back">
                        ← Volver al ranking
                      </button>
                      <h2 className="text-xl font-semibold mt-1">🏪 {wholesaleDetailClient.name}</h2>
                    </div>
                  </div>

                  {/* Sub-cuentas (padre + hijos) */}
                  <div className="bg-white border rounded-sm">
                    <div className="p-3 border-b bg-slate-50 text-xs font-semibold uppercase text-slate-600">Padre + Sub-cuentas</div>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left">Cuenta</th>
                          <th className="px-3 py-2 text-right">Ventas</th>
                          <th className="px-3 py-2 text-right">Piezas</th>
                          <th className="px-3 py-2 text-right">Importe</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {((wholesaleDetail.ranking || [])[0]?.by_child || []).map(cb => (
                          <tr key={cb.client_id} data-testid={`wholesale-child-${cb.client_id}`}>
                            <td className="px-3 py-2">
                              {cb.is_child ? <span className="text-amber-600 mr-1">↳</span> : <span className="mr-1">👤</span>}
                              {cb.name}
                              {cb.is_child && <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">HIJO</span>}
                            </td>
                            <td className="px-3 py-2 text-right">{cb.count_sales}</td>
                            <td className="px-3 py-2 text-right font-bold text-blue-700">{cb.total_qty}</td>
                            <td className="px-3 py-2 text-right text-violet-700 font-semibold">${(cb.total_amount||0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Detalle productos */}
                  <div className="bg-white border rounded-sm">
                    <div className="p-3 border-b bg-slate-50 text-xs font-semibold uppercase text-slate-600">Productos comprados en el rango</div>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left">Producto</th>
                          <th className="px-3 py-2 text-left">SKU</th>
                          <th className="px-3 py-2 text-right">Piezas</th>
                          <th className="px-3 py-2 text-right">Importe</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {(wholesaleDetail.detail || []).map((d, idx) => (
                          <tr key={idx} data-testid={`wholesale-product-${idx}`}>
                            <td className="px-3 py-2 flex items-center gap-2">
                              {d.image_url ? <img src={d.image_url} alt="" className="w-7 h-7 rounded object-cover border" loading="lazy" /> : <div className="w-7 h-7 rounded bg-slate-100" />}
                              <span>{d.product_name}</span>
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-500">{d.sku || '—'}</td>
                            <td className="px-3 py-2 text-right font-bold text-blue-700">{d.qty}</td>
                            <td className="px-3 py-2 text-right text-violet-700 font-semibold">${(d.amount||0).toFixed(2)}</td>
                          </tr>
                        ))}
                        {(!wholesaleDetail.detail || wholesaleDetail.detail.length === 0) && (
                          <tr><td colSpan="4" className="px-4 py-8 text-center text-muted-foreground">Sin productos en el rango</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Top Zonas / Heatmap */}
          <TabsContent value="heatmap">
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Rango controlado por el selector de fechas arriba ({fromDate} → {toDate}).
                {heatmapLoading && ' · cargando…'}
              </p>

              {heatmap && (
                <>
                  {/* Resumen */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="heatmap-summary">
                    <div className="bg-white border rounded p-3">
                      <div className="text-xs text-muted-foreground">Envíos totales</div>
                      <div className="text-2xl font-bold">{heatmap.total_shipments}</div>
                    </div>
                    <div className="bg-white border rounded p-3">
                      <div className="text-xs text-muted-foreground">Revenue items</div>
                      <div className="text-2xl font-bold text-emerald-700">${heatmap.total_items_revenue.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</div>
                    </div>
                    <div className="bg-white border rounded p-3">
                      <div className="text-xs text-muted-foreground">Envíos cobrados (avg)</div>
                      <div className="text-2xl font-bold text-sky-700">${heatmap.avg_shipping.toFixed(2)}</div>
                    </div>
                    <div className="bg-white border rounded p-3">
                      <div className="text-xs text-muted-foreground">Sin identificar</div>
                      <div className="text-2xl font-bold text-amber-700">{heatmap.unidentified_count}</div>
                    </div>
                  </div>

                  {/* Heatmap por barras */}
                  <div className="bg-white border rounded overflow-hidden" data-testid="heatmap-zones-table">
                    <div className="p-3 border-b flex items-center justify-between">
                      <h2 className="text-lg font-semibold">📍 Top zonas por número de envíos</h2>
                      <span className="text-xs text-muted-foreground">{heatmap.zones.length} zonas</span>
                    </div>
                    {heatmap.zones.length === 0 ? (
                      <p className="p-6 text-center text-muted-foreground text-sm">Sin envíos en el rango seleccionado.</p>
                    ) : (
                      <div className="divide-y">
                        {(() => {
                          const maxCount = Math.max(...heatmap.zones.map(z => z.count), 1);
                          return heatmap.zones.map((z, idx) => {
                            const pct = (z.count / maxCount) * 100;
                            const isTop = idx === 0 && z.colonia !== 'Sin identificar';
                            const isUnid = z.colonia === 'Sin identificar';
                            const barColor = isUnid ? 'bg-amber-200' : (isTop ? 'bg-emerald-300' : 'bg-violet-200');
                            return (
                              <div
                                key={z.colonia}
                                className="relative px-4 py-3 hover:bg-slate-50"
                                data-testid={`heatmap-zone-${z.colonia}`}
                              >
                                {/* Barra de fondo */}
                                <div
                                  className={`absolute inset-y-0 left-0 ${barColor} opacity-40`}
                                  style={{ width: `${pct}%` }}
                                />
                                <div className="relative flex items-center justify-between gap-4 flex-wrap">
                                  <div className="flex items-center gap-3 min-w-0">
                                    <span className="w-6 text-center text-xs font-semibold text-slate-500">#{idx + 1}</span>
                                    <div className="min-w-0">
                                      <div className={`font-semibold truncate ${isUnid ? 'text-amber-800' : ''}`}>
                                        {isTop && '🏆 '}
                                        {isUnid && '⚠️ '}
                                        {z.colonia}
                                      </div>
                                      {isUnid && z.sample_addresses && z.sample_addresses.length > 0 && (
                                        <div className="text-[10px] text-muted-foreground truncate">
                                          Ej: {z.sample_addresses[0]}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-6 text-sm">
                                    <div className="text-right">
                                      <div className="font-bold text-lg leading-none">{z.count}</div>
                                      <div className="text-[10px] text-muted-foreground">envíos</div>
                                    </div>
                                    <div className="text-right hidden sm:block">
                                      <div className="font-semibold text-emerald-700">${z.items_total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</div>
                                      <div className="text-[10px] text-muted-foreground">items</div>
                                    </div>
                                    <div className="text-right hidden md:block">
                                      <div className="font-semibold text-sky-700">${z.avg_shipping.toFixed(2)}</div>
                                      <div className="text-[10px] text-muted-foreground">avg envío</div>
                                    </div>
                                    <div className="text-right hidden lg:block text-xs text-muted-foreground">
                                      {z.paid_count > 0 && <div>✅ {z.paid_count} pagados</div>}
                                      {z.unassigned_count > 0 && <div className="text-rose-600">⚠️ {z.unassigned_count} sin asignar</div>}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>

                  {heatmap.unidentified_count > 0 && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                      💡 Hay <b>{heatmap.unidentified_count}</b> envío(s) cuya dirección no coincide con ninguna colonia registrada en <a href="/shipping-costs" className="underline">Costos de envío</a>. Agrega esas colonias para tener mejor visibilidad.
                    </p>
                  )}
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};
